import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchProductsPage } from "@/lib/api";
import { MediaTile } from "@/components/MediaTile";
import { Skeleton } from "@/components/ui/skeleton";

const OCCASIONS = [
  { category: "gold", label: "Gold" },
  { category: "diamond", label: "Diamond" },
  { category: "silver", label: "Silver" },
  { category: "others", label: "Others" },
];

/**
 * Big 2x2 lifestyle-style banner grid, one tile per main category, each
 * showing a real current product photo pulled from the Admin panel (not a
 * hardcoded static image, so it always reflects what's actually in stock).
 * Clicking a tile opens that category's full catalog page. Falls back to a
 * plain gold gradient tile with just the label if no photo exists yet for
 * that category.
 */
export const OccasionGrid = () => {
  const [tiles, setTiles] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const results = await Promise.all(
        OCCASIONS.map(async (o) => {
          try {
            const items = await fetchProductsPage({ category: o.category, limit: 1 });
            return { ...o, item: items?.[0] || null };
          } catch {
            return { ...o, item: null };
          }
        })
      );
      if (!cancelled) setTiles(results);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section className="relative py-14 sm:py-20 bg-background">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10 sm:mb-14">
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-foreground">Shop by Occasion</h2>
          <p className="mt-3 text-sm sm:text-base text-muted-foreground">A companion for every occasion</p>
        </div>

        {!tiles ? (
          <div className="grid grid-cols-2 gap-3 sm:gap-6">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="aspect-square sm:aspect-[4/3] rounded-2xl" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:gap-6">
            {tiles.map(({ category, label, item }) => (
              <button
                key={category}
                onClick={() => navigate(`/all-items?category=${category}`)}
                className="group relative aspect-square sm:aspect-[4/3] rounded-xl sm:rounded-2xl overflow-hidden text-left"
                data-testid={`occasion-tile-${category}`}
              >
                {item?.media_url ? (
                  <MediaTile
                    src={item.media_url}
                    mediaType={item.media_type}
                    alt={label}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                ) : (
                  <div className="absolute inset-0" style={{ background: "var(--gradient-gold)" }} />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                <span className="absolute bottom-3 left-4 sm:bottom-5 sm:left-6 font-heading text-lg sm:text-3xl font-semibold text-white">
                  {label}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
